import {
  useGetContrarianSignalLatestQuery,
  useContrarianRunNowMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { toast } from "sonner";

/**
 * ContrarianSignalCard — fade-crowded-positioning signal for BTC.
 *
 * Sources: Binance futures L/S ratio + funding + OI + estimated
 * liquidation zones. Output: bullish/bearish score 0-100, trade
 * direction (LONG / SHORT_BIAS / NEUTRAL), entry zone + stop + targets.
 *
 * SHORT_BIAS is analysis-only on this spot bot — surfaces as context
 * for trim decisions, not a sell signal. LONG signals at high
 * confidence are real entries; the gatekeeper still gets veto.
 */
export function ContrarianSignalCard() {
  const q = useGetContrarianSignalLatestQuery({}, { pollingInterval: 60000 });
  const [runNow, runState] = useContrarianRunNowMutation();
  const sig: any = (q.data?.signals || [])[0];

  // snapshot_json is persisted as a string — parse to surface per-source breakdown
  let snap: any = null;
  try { snap = sig?.snapshot_json ? JSON.parse(sig.snapshot_json) : null; } catch { snap = null; }
  const sourcesUsed: string[] = snap?.sources_used || [];
  const perSource: Record<string, any> = snap?.per_source || {};

  async function onRun() {
    try {
      await runNow({}).unwrap();
      await q.refetch();
      toast.success("Contrarian signal refreshed");
    } catch (e: any) {
      toast.error(`run failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span>Contrarian sentiment · BTC</span>
          <StalenessBadge ts={sig?.ts} warnHours={2} staleHours={6} veryStaleHours={24} />
          <span className="text-xs text-muted font-normal">
            fades crowded futures positioning · feeds governor as a regime signal
          </span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={onRun} disabled={runState.isLoading}>
          {runState.isLoading ? "Running…" : "Refresh"}
        </Button>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : !sig ? (
          <div className="text-sm text-muted">
            No signal yet. Click <em>Refresh</em> or wait for the hourly cron.
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {/* Headline */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="Direction" value={sig.trade_direction || "—"}
                    tone={sig.trade_direction === "LONG" ? "good"
                          : sig.trade_direction === "SHORT_BIAS" ? "bad"
                          : "muted"} />
              <Tile label="Confidence" value={`${Number(sig.confidence ?? 0).toFixed(0)}/100`}
                    tone={(sig.confidence ?? 0) >= 65 ? "good"
                          : (sig.confidence ?? 0) >= 40 ? "warn" : "muted"} />
              <Tile label="Regime" value={sig.regime || "—"}
                    tone={String(sig.regime || "").includes("bull") ? "good"
                          : String(sig.regime || "").includes("bear") ? "bad" : "muted"} />
              <Tile label="Tradability"
                    value={`${(Number(sig.tradability ?? 0) * 100).toFixed(0)}%`}
                    tone={(sig.tradability ?? 0) >= 0.7 ? "good"
                          : (sig.tradability ?? 0) >= 0.3 ? "warn" : "bad"} />
            </div>

            {/* Score breakdown */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="border border-border rounded p-2 bg-bg2">
                <div className="text-muted mb-1">Bullish (fade shorts)</div>
                <div className="text-xl font-bold tabular-nums">
                  {Number(sig.bullish_score ?? 0).toFixed(1)}
                  <span className="text-xs text-muted ml-1">/100</span>
                </div>
              </div>
              <div className="border border-border rounded p-2 bg-bg2">
                <div className="text-muted mb-1">Bearish (fade longs)</div>
                <div className="text-xl font-bold tabular-nums">
                  {Number(sig.bearish_score ?? 0).toFixed(1)}
                  <span className="text-xs text-muted ml-1">/100</span>
                </div>
              </div>
            </div>

            {/* Multi-source breakdown — only when 2+ exchanges contributed */}
            {sourcesUsed.length >= 2 && (
              <div>
                <div className="text-xs text-muted mb-1">
                  Composite sources: {sourcesUsed.join(" + ")}
                  <span className="ml-1 text-muted/70">
                    (scorer reads the mean — agreement across venues strengthens the signal)
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {sourcesUsed.map((src) => {
                    const s = perSource[src] || {};
                    const long = s.retail_long_pct;
                    const fund = s.funding_rate_8h_avg;
                    const oi24 = s.oi_24h_change_pct;
                    return (
                      <div key={src} className="border border-border/40 rounded p-2 bg-bg2">
                        <div className="text-muted mb-1 uppercase tracking-wide">{src}</div>
                        <div className="grid grid-cols-3 gap-1">
                          <div>
                            <div className="text-muted">L/S</div>
                            <div className="font-mono">
                              {long != null ? `${Number(long).toFixed(1)}%` : "—"}
                            </div>
                          </div>
                          <div>
                            <div className="text-muted">Fund 8h</div>
                            <div className="font-mono">
                              {fund != null ? `${(Number(fund) * 100).toFixed(4)}%` : "—"}
                            </div>
                          </div>
                          <div>
                            <div className="text-muted">OI 24h</div>
                            <div className="font-mono">
                              {oi24 != null
                                ? `${Number(oi24) > 0 ? "+" : ""}${Number(oi24).toFixed(2)}%`
                                : "—"}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Inputs that drove the score */}
            <div>
              <div className="text-xs text-muted mb-1">
                Inputs
                {sourcesUsed.length === 1 && (
                  <span className="ml-1 text-muted/70">
                    (single source: {sourcesUsed[0]})
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <Pair label="Retail long" value={`${Number(sig.retail_long_pct ?? 0).toFixed(1)}%`} />
                <Pair label="Retail short" value={`${Number(sig.retail_short_pct ?? 0).toFixed(1)}%`} />
                <Pair label="Funding 8h"
                       value={sig.funding_rate_current != null
                              ? `${(Number(sig.funding_rate_current) * 100).toFixed(4)}%`
                              : "—"} />
                <Pair label="OI 24h Δ"
                       value={sig.oi_24h_change_pct != null
                              ? `${Number(sig.oi_24h_change_pct) > 0 ? "+" : ""}${Number(sig.oi_24h_change_pct).toFixed(2)}%`
                              : "—"} />
                <Pair label="ADX" value={sig.adx != null ? Number(sig.adx).toFixed(1) : "—"} />
                <Pair label="ATR%" value={sig.atr_pct != null ? `${Number(sig.atr_pct).toFixed(2)}%` : "—"} />
                <Pair label="Last" value={sig.last_close != null ? `$${Number(sig.last_close).toLocaleString()}` : "—"} />
                <Pair label="Gov contrib"
                       value={sig.governor_contribution != null
                              ? `${Number(sig.governor_contribution) > 0 ? "+" : ""}${Number(sig.governor_contribution).toFixed(1)}`
                              : "—"} />
              </div>
            </div>

            {/* Trade plan — only when there's a direction */}
            {sig.trade_direction && sig.trade_direction !== "NEUTRAL" && sig.stop_loss && (
              <div className="border border-border rounded p-3 bg-bg2">
                <div className="text-xs text-muted mb-1">
                  {sig.trade_direction === "LONG"
                   ? "Long entry plan"
                   : "Short-bias context (spot-only bot — analysis-only)"}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <Pair label="Entry"
                         value={`$${Number(sig.entry_zone_low ?? 0).toLocaleString()}–$${Number(sig.entry_zone_high ?? 0).toLocaleString()}`} />
                  <Pair label="Stop" value={`$${Number(sig.stop_loss).toLocaleString()}`} />
                  <Pair label="Target 1" value={`$${Number(sig.target_1 ?? 0).toLocaleString()}`} />
                  <Pair label="Target 2" value={`$${Number(sig.target_2 ?? 0).toLocaleString()}`} />
                  <Pair label="R:R" value={sig.risk_reward != null ? Number(sig.risk_reward).toFixed(2) : "—"} />
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone: "good" | "warn" | "bad" | "muted" }) {
  return (
    <div className="border border-border rounded p-2 bg-bg2">
      <div className="text-xs text-muted">{label}</div>
      <div className="font-semibold">
        <Badge tone={tone}>{value}</Badge>
      </div>
    </div>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between border border-border/40 rounded px-2 py-1">
      <span className="text-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
